import { Component, ViewChild, input, output } from '@angular/core';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { Medication, PipesModule } from '@xmedical/core';
import { WithReferralInfo } from '../../types/referral-info';
import { MedicationListViewComponent } from './medication-list-view.component';

/**
 * Generic interface for displayed medication items.
 */
export interface MedicationDisplayItem extends WithReferralInfo {
  id?: number;
  description: string;
  dosage?: string;
  diagnosisCode?: string;
  comment?: string;
  lastModifiedDate?: Date;
  lastModifiedUser?: { firstName?: string; surname?: string };
  // For duplicate type detection
  typeId?: number;
  typeUnassigned?: boolean;
  // Optional text color
  textColour?: string;
}

/**
 * Event emitted when a medication is added from search results.
 */
export interface MedicationAddedEvent {
  item: Medication;
  dosage: string;
  diagnosisCode: string;
  comment: string;
}

/**
 * Event emitted when a medication is removed from the list.
 */
export interface MedicationRemovedEvent {
  item: MedicationDisplayItem;
  index: number;
}

@Component({
  selector: 'xm-medication-widget-view',
  templateUrl: './medication-widget-view.component.html',
  styleUrls: ['./medication-widget-view.component.scss'],
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
    PipesModule,
    MedicationListViewComponent
  ]
})
export class MedicationWidgetViewComponent {
  @ViewChild(AppMatAutocompleteComponent) autocomplete!: AppMatAutocompleteComponent;

  // Inputs
  items = input<MedicationDisplayItem[]>([]);
  title = input<string>('Medication');
  searchFn = input.required<(query: string) => Observable<Medication[]>>();
  isProcessing = input<boolean>(false);
  canAdd = input<boolean>(true);
  canDelete = input<boolean>(true);
  addItemText = input<string>('Add new medication');
  usePrimaryText = input<boolean>(false);

  // Outputs
  itemAdded = output<MedicationAddedEvent>();
  itemRemoved = output<MedicationRemovedEvent>();
  addNewItem = output<string>();

  // Track comment for search results before adding
  resultComments: { [key: number]: string } = {};

  getResultData(itemId: number): { comment: string } {
    if (!this.resultComments[itemId]) {
      this.resultComments[itemId] = '';
    }
    return { comment: this.resultComments[itemId] };
  }

  setResultComment(itemId: number, value: string): void {
    this.resultComments[itemId] = value;
  }

  onItemSelected(result: Medication): void {
    const comment = this.resultComments[result.id] || '';
    this.itemAdded.emit({
      item: result,
      dosage: result.dosage || '',
      diagnosisCode: result.diagnosisCode || '',
      comment
    });
    this.clearSearch();
  }

  onRemoveItem(item: MedicationDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  onAddNewItem(): void {
    const searchTerm = this.autocomplete?.queryText || '';
    this.clearSearch();  // Close search panel
    this.addNewItem.emit(searchTerm);
  }

  private clearSearch(): void {
    this.resultComments = {};
    if (this.autocomplete) {
      this.autocomplete.cancelSearch();
    }
  }

}
